import React, { useState } from 'react';
import Webcam from 'react-webcam';
import logo from '../../assets/images/sample_144x144.jpg'
import maskImage from '../../assets/images/Mask.png'
import { useTranslation } from 'react-i18next';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { GuidanceModal, Slide } from '../../components/Modals/GuidanceModal';

const THEME = '#c67b05';
const THEME_SHADOW = 'rgba(198,123,5,0.32)';

interface IPhotoProps {
    image: string | null;
    isPhotoCroped: boolean;
    PhotoBlob: Blob | null;
    useCam: boolean;
    startSelfieCamera: () => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    cropperPhotoRef: React.RefObject<ReactCropperElement>;
    resetImage: () => void;
    handleZoomIn: () => void;
    handleZoomOut: () => void;
    rotateImage: () => void;
    getCropData: () => void;
    webcamRef: React.RefObject<Webcam>;
    captureImageFromWebcam: () => void;
    isMobile: boolean;
    showFaceError: boolean;
    faceError: string;
}

export const PhotoUpload = ({
    image,
    isPhotoCroped,
    PhotoBlob,
    useCam,
    startSelfieCamera,
    onChange,
    cropperPhotoRef,
    resetImage,
    handleZoomIn,
    handleZoomOut,
    getCropData,
    webcamRef,
    captureImageFromWebcam,
    isMobile,
    showFaceError,
    rotateImage,
    faceError
}: IPhotoProps) => {
    const { t } = useTranslation();


    const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);

    const photoSlides: Slide[] = [
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep1Title', 'Acceptable Photo'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep1Desc', 'Full face, centered in frame with space all around. Facing camera with shoulders square. Solid background. No hat or sunglasses.'),
            isAcceptable: true,
            image: logo
        },
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep2Title', 'Use Solid Color Background'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep2Desc', 'Avoid busy, outdoor, or textured backgrounds. Use a solid color background.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/no-solid-bg.png`
        },
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep3Title', 'Look at Camera Face-on'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep3Desc', 'Do not look away or stand at an angle. Look directly at the camera face-on.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/no-side-pose.png`
        },
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep4Title', 'Remove Hat or Sunglasses'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep4Desc', 'Remove any hats, caps, or sunglasses before taking your photo.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/no-hat.png`
        },
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep4bTitle', 'Remove Sunglasses'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep4bDesc', 'Remove any sunglasses before taking your photo.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/no-sunglasses.png`
        },
        {
            title: t('PhotoManagement.PhotoUpload.GuidanceStep5Title', 'Center Head in Frame'),
            description: t('PhotoManagement.PhotoUpload.GuidanceStep5Desc', 'Do not position your head too low or too close. Center your head in the frame with space all around.'),
            isAcceptable: false,
            image: `${process.env.PUBLIC_URL || ''}/guideline-images/head-in-center.png`
        }
    ];
    return (
        <div style={{ width: '100%' }}>
    

            {/* Post-crop Actions & Guidance */}
            <div style={{ marginTop: '20px' }}>
                {showFaceError && (
                    <div className="cco_form_message cco_form_message_error" style={{ marginBottom: 10 }}>
                        {faceError ? faceError : t('PhotoManagement.PhotoUpload.PhotoRec')}
                    </div>
                )}

                {isPhotoCroped &&
                    <div style={{ marginBottom: '15px' }}>
                        <button onClick={resetImage} style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', color: '#555', fontWeight: 500, transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {t('PhotoManagement.PhotoUpload.Reset')}
                        </button>
                    </div>
                }

                {isMobile && !image && (
                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        <img src={logo} width={180} alt="Sample Profile" style={{ borderRadius: '8px', opacity: 0.8 }} />
                    </div>
                )}

                <div style={{ marginTop: '10px' }}>
                    {isMobile && (
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); setIsGuidanceOpen(true); }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#d19b3d', fontWeight: 600, textDecoration: 'none' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                            {t('PhotoManagement.PhotoUpload.Submit')}
                        </a>
                    )}
                </div>

                <GuidanceModal isOpen={isGuidanceOpen} onClose={() => setIsGuidanceOpen(false)} slides={photoSlides} hideUnacceptableBadge={true} />
            </div>


            {useCam && (
<div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '100dvh', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', backdropFilter: 'blur(5px)' }}>                   
     <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '95%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '22px', color: '#1e293b', fontWeight: 600 }}>{t('PhotoManagement.PhotoUpload.TakePhoto')}</h3>

                        <div style={{ width: '100%', aspectRatio: '5/7', borderRadius: '12px', overflow: 'hidden', background: '#000', marginBottom: '20px', display: 'flex', justifyContent: 'center', position: 'relative' }}>
                            <Webcam
                                audio={false}
                                ref={webcamRef}
                                mirrored={true}
                                screenshotFormat='image/jpeg'
                                screenshotQuality={1}
                                videoConstraints={{ facingMode: 'user', width: { ideal: 1440 }, height: { ideal: 1080 } }}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <img src={maskImage} alt="Face Alignment Mask" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', opacity: 0.85 }} />
                        </div>

                        <div style={{ display: 'flex', gap: '15px', width: '100%' }}>
                            <button
                                onClick={resetImage}
                                style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '8px', color: '#475569', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s', fontSize: '15px' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                            >
                                {t('PhotoManagement.PhotoUpload.Close')}
                            </button>
                            <button
                                onClick={captureImageFromWebcam}
                                style={{ flex: 1, padding: '12px', background: '#d29a46', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#b88133'}
                                onMouseLeave={e => e.currentTarget.style.background = '#d29a46'}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="4"></circle><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path></svg>
                                {t('PhotoManagement.PhotoUpload.Capture')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div >
    );
};

